// src/features/feeds/components/FeedPublishModal.tsx

import { useState, useMemo } from 'react';
import {
  Modal, Stack, Select, TextInput, Button, Group,
  CopyButton, ActionIcon, Tooltip,
} from '@mantine/core';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { FeedLinker } from '@lib2/model/player/FeedLinker';
import { FeedFormat } from '@lib2/model/feed/FeedTypes';
import { Hostname } from '@lib2/model/common/ServiceHosts';
import { usePlayers, setStoredPlayerId } from '@/features/players/usePlayers';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { FEED_FORMATS, FEED_FORMAT_LABELS, type FeedFormat as FeedFormatType } from '@/lib/constants/feed';
import type { FeedData } from '@/lib/types/Feed';

interface FeedPublishModalProps {
  opened: boolean;
  onClose: () => void;
  feed: FeedData;
}

const MONO_STYLE = { input: { fontFamily: 'monospace', fontSize: 12 } } as const;

/** Map our constant format values to lib2 FeedFormat enum. */
function toLib2Format(format: FeedFormatType): FeedFormat {
  switch (format) {
    case 'mrss': return FeedFormat.Mrss;
    case 'Roku': return FeedFormat.Roku;
    case 'firetv': return FeedFormat.FireTV;
    default: return FeedFormat.Mrss;
  }
}

const FORMAT_OPTIONS = FEED_FORMATS.map((f) => ({
  value: f,
  label: FEED_FORMAT_LABELS[f],
}));

/** Read-only field with integrated one-click copy icon. */
function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <TextInput
      label={label}
      value={value}
      readOnly
      styles={MONO_STYLE}
      rightSectionWidth={36}
      rightSection={
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="left">
              <ActionIcon
                variant="subtle"
                color={copied ? 'teal' : 'gray'}
                onClick={copy}
                size="sm"
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}

export function FeedPublishModal({ opened, onClose, feed }: FeedPublishModalProps) {
  const { selectedCId } = useAuth();
  const { defaultPlayerId, playerOptions } = usePlayers();

  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [feedFormat, setFeedFormat] = useState<FeedFormatType>('mrss');

  // Resolve effective player: local selection > hook default
  const effectivePlayerId = selectedPlayerId ?? defaultPlayerId;

  const isReseller = Hostname.isResellerHostname();

  // Generate feed URL
  const feedUrl = useMemo(() => {
    if (!effectivePlayerId) return null;
    const linker = new FeedLinker(feed.cId, effectivePlayerId, feed.feedId, isReseller);
    return linker.getFeedUrl(toLib2Format(feedFormat));
  }, [feed.cId, feed.feedId, effectivePlayerId, feedFormat, isReseller]);

  const handlePlayerChange = (val: string | null) => {
    if (!val) return;
    const id = parseInt(val, 10);
    setSelectedPlayerId(id);
    if (selectedCId) setStoredPlayerId(selectedCId, id);
  };

  if (!effectivePlayerId || !feedUrl) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Publish Feed "${feed.title}"`}
      size="md"
    >
      <Stack gap="md">
        {/* Player selector */}
        <Select
          label="Player"
          data={playerOptions}
          value={String(effectivePlayerId)}
          onChange={handlePlayerChange}
          allowDeselect={false}
        />

        {/* Feed format selector */}
        <Select
          label="Feed Format"
          data={FORMAT_OPTIONS}
          value={feedFormat}
          onChange={(val) => val && setFeedFormat(val as FeedFormatType)}
          allowDeselect={false}
        />

        {/* Feed URL */}
        <CopyableField label="Feed URL" value={feedUrl} />

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
